import { FC } from 'react';
import { Helmet, HelmetProps } from 'react-helmet';
import { graphql, useStaticQuery } from 'gatsby';

interface HeadProps extends HelmetProps {
  title?: string;
  description?: string;
  keywords?: readonly string[];
  featuredImageUrl?: string;
  currentUrl: string;
  canonicalUrl?: string | null;
  jsonLd?: string;
}

export const Head: FC<HeadProps> = ({
  title,
  description,
  keywords,
  featuredImageUrl,
  currentUrl,
  canonicalUrl,
  jsonLd,
}) => {
  const data = useStaticQuery<Queries.HeadQuery>(graphql`
    query Head {
      site {
        siteMetadata {
          title
          description
          author
          siteUrl
          defaultKeywords
        }
      }
    }
  `);

  const site = data.site!;
  const { siteMetadata } = site;
  const { siteUrl, author, defaultKeywords } = site.siteMetadata;
  const metaDescription = description || siteMetadata.description;
  const imageUrl = featuredImageUrl || '/lmn-logo.png';
  const seoKeywords: readonly (string | null)[] = keywords || defaultKeywords!;

  return (
    <Helmet
      htmlAttributes={{ lang: 'en' }}
      title={title || `${author} ${siteMetadata.title}`}
      titleTemplate={`%s | ${siteMetadata.title}`}
      meta={[
        {
          name: 'description',
          content: metaDescription,
        },
        {
          name: 'viewport',
          content: 'width=device-width, initial-scale=1',
        },
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:description',
          content: metaDescription,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:image',
          content: `${siteUrl}${imageUrl}`,
        },
        {
          name: 'twitter:card',
          content: 'summary',
        },
        {
          name: 'twitter:creator',
          content: author!,
        },
        {
          name: 'twitter:title',
          content: title,
        },
        {
          name: 'twitter:description',
          content: metaDescription,
        },
        {
          property: 'twitter:image',
          content: imageUrl,
        },
        {
          name: 'keywords',
          content: seoKeywords.map((keyword) => keyword!.replace(/-/g, ' ')).join(', '),
        },
        {
          name: 'google-site-verification',
          content: 'HJID6-Zd_4bYcnQci5bBjuZtilFijNZ190x4A1N9XDg',
        },
      ]}
    >
      <link rel="canonical" href={canonicalUrl || currentUrl} />
      {jsonLd && <script type="application/ld+json">{jsonLd}</script>}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link
        href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap"
        rel="stylesheet"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&display=swap"
        rel="stylesheet"
      />
    </Helmet>
  );
};
